/* eslint import/prefer-default-export: "off" */
import UriJs from 'urijs';
import _ from 'lodash';
import getStateFullName from './stateNames';
import {navigate} from './historyManager';

const UPDATE_PARAM_PUSH = 1;
const UPDATE_PARAM_REPLACE = 2;
const UPDATE_PARAM_IGNORE = 3;

export function getQueryParam(paramName, location = window.location.href) {
  const url = new UriJs(location);
  const value = _.get(url.search(true), [paramName], null);
  let output = value;
  try {
    output = JSON.parse(decodeURIComponent(output));
  }
  catch (e) {
    // Do nothing.
  }
  return output;
}

/** True when URL indicates opening a specific property (deep link), not a market search. */
export function hasPropertyUrlIntent(search = typeof window !== 'undefined' ? window.location.search : '') {
  if (!search) {
    return false;
  }
  const href = search.startsWith('?') ? `http://x${search}` : `http://x/?${search}`;
  return Boolean(getQueryParam('folio', href) || getQueryParam('property_id', href));
}

/** True when URL should open the property panel for a specific property. */
export function hasPropertyPanelQueryIntent(search = typeof window !== 'undefined' ? window.location.search : '') {
  if (!hasPropertyUrlIntent(search)) {
    return false;
  }
  const leftOverlay = new URLSearchParams(search || '').get('leftOverlay');
  const normalizedLeftOverlay = leftOverlay == null ? '' : String(leftOverlay).toLowerCase();
  return !normalizedLeftOverlay || normalizedLeftOverlay === 'properties';
}

export function isExpectedAbortError(error) {
  const message = String(error?.message || error?.networkError?.message || error || '');
  return (
    error?.name === 'AbortError'
    || error?.name === 'CanceledError'
    || error?.code === 'ERR_CANCELED'
    || message.includes('The user aborted a request')
  );
}

const applyNewRoute = (str, updateParam) => {
  switch (updateParam) {
    case UPDATE_PARAM_PUSH:
      navigate(str, 'push', {page: window.history.length});
      break;
    case UPDATE_PARAM_REPLACE:
      navigate(str, 'replace', {page: window.history.length});
      break;
    case UPDATE_PARAM_IGNORE:
    default:
    // Nothing.
  }
};

export function setQueryParamArray(params = [], update = UPDATE_PARAM_PUSH, location) {
  const newUrl = new UriJs(location || window.location.href);
  params.forEach(({name, value}) => {
    if (
      (typeof value === 'object' && !_.isEmpty(value))
      || (typeof value !== 'object' && value)
      || typeof value === 'boolean'
    ) {
      const valueString = _.isObject(value) ? JSON.stringify(value) : value;
      newUrl.setSearch({[name]: valueString});
    }
    else {
      newUrl.removeSearch(name);
    }
  });

  applyNewRoute(newUrl.readable().toString(), update);
}

export function setQueryParam(paramName, paramValue, update = UPDATE_PARAM_PUSH, location) {
  setQueryParamArray([{name: paramName, value: paramValue}], update, location);
}

/**
 * Build a crawlable href for a 1-based pager page number, preserving the current
 * path and other query params. Page one drops the param to keep the URL clean.
 */
export function buildPageParamHref(paramName, pageNum, location = window.location.href) {
  const url = new UriJs(location);
  if (pageNum > 1) {
    url.setSearch(paramName, pageNum);
  }
  else {
    url.removeSearch(paramName);
  }
  return `${url.path()}${url.search()}`;
}

export const getStringTitle = title => (typeof title === 'string' ? title : title?.[0] || '');

export const getPlaceDisplayName = place => place?.text || (place?.title != null ? getStringTitle(place.title) : '');

const PROPERTY_SHORT_PREFIX = '/zoning-detail/';

export function parsePropertyAlias(alias) {
  if (!alias || typeof alias !== 'string') {
    return null;
  }
  const dash = alias.indexOf('-');
  if (dash <= 0 || dash === alias.length - 1) {
    return null;
  }
  return {groupId: alias.slice(0, dash), folio: alias.slice(dash + 1)};
}

export function buildPropertyShortPath(groupId, folio) {
  if (!groupId || !folio) {
    return null;
  }
  return `${PROPERTY_SHORT_PREFIX}${groupId}-${folio}`;
}

/**
 * Query params that describe the property/results panel view itself. The property short path
 * (`/zoning-detail/{groupId}-{folio}`) encodes that view in its pathname, so these params must be
 * dropped when carrying a search string onto it. Everything else — map filters (`_tags_*`, place,
 * search polygon), 3D/style/layers view state — is preserved so opening a property from a filtered
 * market keeps the filters alive in the URL (mirrors the long `?folio=` URL, which keeps them too).
 */
const PROPERTY_PANEL_SCOPED_PARAMS = ['folio', 'property_id', 'leftOverlay', 'activeTab', 'propertyActiveTab'];

export function preserveMapViewSearch(search = typeof window !== 'undefined' ? window.location.search : '') {
  if (!search) {
    return '';
  }
  const params = new URLSearchParams(search);
  PROPERTY_PANEL_SCOPED_PARAMS.forEach(name => params.delete(name));
  const next = params.toString();
  return next ? `?${next}` : '';
}

export function syncPropertyShortPath(history, groupId, folio) {
  const shortPath = buildPropertyShortPath(groupId, folio);
  if (!shortPath) {
    return false;
  }
  // Keep the active filters/view state in the URL when replacing to the selected parcel — the short
  // path drops the query string otherwise, wiping applied filters from a filtered market.
  const target = `${shortPath}${preserveMapViewSearch()}`;
  if (target === `${window.location.pathname}${window.location.search || ''}`) {
    return false;
  }
  if (history && typeof history.replace === 'function') {
    history.replace(target);
  }
  else {
    navigate(target, 'replace', {page: window.history.length});
  }
  return true;
}

export function isPropertyShortPath(pathname = window.location.pathname) {
  return typeof pathname === 'string' && pathname.indexOf(PROPERTY_SHORT_PREFIX) === 0;
}

/**
 * Remove property-specific query params (folio, property_id) from a search string.
 * A place/market URL must never carry a folio from a previously-viewed property: when navigating
 * into a new market (e.g. clicking the map grid) the preserved search can fall back to a stale
 * react-router location.search that still holds the old folio, which re-opens the wrong property.
 */
export function stripPropertyQueryParams(search = '') {
  if (!search) {
    return '';
  }
  const params = new URLSearchParams(search);
  params.delete('folio');
  params.delete('property_id');
  const next = params.toString();
  return next ? `?${next}` : '';
}

/**
 * True when the pathname is served by the map (App.js routes `/zoning-detail/:alias` and
 * `/:routeType/us/*`). Used to suppress map-driven URL pushes once the user has navigated to a
 * non-map route (e.g. /crm/notes) — otherwise a late breadcrumb effect would clobber that route.
 * `/browse/us/*` is its own (non-map) route, so it is excluded.
 */
export function isMapRoutePath(pathname = window.location.pathname) {
  if (typeof pathname !== 'string') {
    return false;
  }
  if (isPropertyShortPath(pathname)) {
    return true;
  }
  return /^\/(?!browse\/)[^/]+\/us(\/|$)/.test(pathname);
}

/**
 * True when the URL opens property detail (short link or folio/property_id query).
 */
export function isPropertyDeepLink(location = window.location) {
  const pathname = location?.pathname ?? window.location.pathname;
  if (isPropertyShortPath(pathname)) {
    return true;
  }
  const href = location?.href ?? window.location.href;
  return !!(getQueryParam('folio', href) || getQueryParam('property_id', href));
}

/**
 * Path passed to GraphQL metaTags — includes search on property deep links.
 */
export function getMetaTagsRequestPath(location = window.location) {
  const pathname = location?.pathname ?? window.location.pathname;
  if (isPropertyDeepLink(location)) {
    const search = location?.search ?? window.location.search ?? '';
    return `${pathname}${search}`;
  }
  return pathname;
}

/**
 * Whether Redux pageData carries metatags safe to apply on a property deep link.
 */
export function shouldSyncMetaTagsFromPageData(pageData) {
  const metatags = _.get(pageData, 'metatags');
  if (!metatags || _.isEmpty(metatags)) {
    return false;
  }
  if (!isPropertyDeepLink()) {
    return true;
  }
  const pathParam = _.get(pageData, 'pathParam', '');
  if (pathParam === '/us' || pathParam === '/zoning-detail') {
    return false;
  }
  return true;
}

export function expandPropertyShortUrl(history, navigationPlace, folio) {
  if (!isPropertyShortPath()) {
    return false;
  }
  const placePath = navigationPlace?.url || navigationPlace?.path;
  if (!placePath) {
    return false;
  }
  const longPath = placePath.startsWith('/') ? placePath : `/${placePath}`;
  // Preserve applied filters / map view state, then the panel-defining params
  // (leftOverlay/activeTab/propertyActiveTab), when switching from short property URL to market URL.
  const current = new URLSearchParams(window.location.search || '');
  const next = new URLSearchParams(preserveMapViewSearch(window.location.search).replace(/^\?/, ''));
  ['leftOverlay', 'activeTab', 'propertyActiveTab'].forEach((key) => {
    const value = current.get(key);
    if (value) {
      next.set(key, value);
    }
  });
  if (folio) {
    next.set('folio', String(folio));
  }
  const search = next.toString();
  const href = search ? `${longPath}?${search}` : longPath;
  if (history && typeof history.replace === 'function') {
    history.replace(href);
  }
  else {
    navigate(href, 'replace', {page: window.history.length});
  }
  localStorage.setItem('city-search', longPath);
  return true;
}

export function setPropertyCanonical(groupId, folio) {
  if (!groupId || !folio) {
    return;
  }
  const shortHref = `${window.location.origin}${buildPropertyShortPath(groupId, folio)}`;
  let $canonical = document.querySelector('link[rel="canonical"]');
  if (!$canonical) {
    $canonical = document.createElement('link');
    $canonical.setAttribute('rel', 'canonical');
    document.head.prepend($canonical);
  }
  $canonical.setAttribute('href', shortHref);
  const $ogUrl = document.querySelector('meta[property="og:url"]');
  if ($ogUrl) {
    $ogUrl.setAttribute('content', shortHref);
  }
}

export function resetCanonicalToCurrentPath() {
  const href = `${window.location.origin}${window.location.pathname}`;
  const $canonical = document.querySelector('link[rel="canonical"]');
  if ($canonical) {
    $canonical.setAttribute('href', href);
  }
  const $ogUrl = document.querySelector('meta[property="og:url"]');
  if ($ogUrl) {
    $ogUrl.setAttribute('content', href);
  }
}

const normalizeMetaTemplateText = (value) => {
  if (typeof value !== 'string') {
    return value;
  }
  return value
    .replace(/\(\s*\)/g, '')
    .replace(/\s+,/g, ',')
    .replace(/\s+([.!?;:])/g, '$1')
    .replace(/,\s*,+/g, ',')
    .replace(/\s+-\s+-\s+/g, ' - ')
    .replace(/\s+-\s*,\s*/g, ' - ')
    .replace(/,\s*-\s*/g, ' - ')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(/^[, ]+|[, ]+$/g, '');
};

const flattenStringValues = (value) => {
  if (_.isArray(value)) {
    return value.flatMap(entry => flattenStringValues(entry));
  }
  if (_.isPlainObject(value)) {
    return Object.values(value).flatMap(entry => flattenStringValues(entry));
  }
  if (value === null || typeof value === 'undefined' || typeof value === 'boolean') {
    return [];
  }
  const stringValue = `${value}`.trim();
  if (!stringValue || stringValue.toLowerCase() === 'array') {
    return [];
  }
  return [stringValue];
};

const pickFirstString = (item, keys) => {
  const firstValue = keys
    .map(key => _.get(item, key))
    .find(rawValue => !_.isNil(rawValue) && flattenStringValues(rawValue).length);
  if (!_.isNil(firstValue)) {
    return [...new Set(flattenStringValues(firstValue))].join(', ');
  }
  return '';
};

const replaceTemplateTokens = (value, tokens) => {
  if (_.isArray(value)) {
    return value.map(entry => replaceTemplateTokens(entry, tokens));
  }
  if (_.isPlainObject(value)) {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = replaceTemplateTokens(value[key], tokens);
      return acc;
    }, {});
  }
  if (typeof value !== 'string') {
    return value;
  }
  let output = value;
  Object.entries(tokens).forEach(([token, tokenValue]) => {
    output = output.split(token).join(tokenValue || '');
  });
  return normalizeMetaTemplateText(output);
};

export function buildPropertyMetatagsFromTemplate(template, propertyItem = {}) {
  if (!template || typeof template !== 'object') {
    return template;
  }
  const folio = pickFirstString(propertyItem, ['folio']);
  const address = pickFirstString(propertyItem, ['address']) || (folio ? `APN ${folio}` : '');
  const city = pickFirstString(propertyItem, ['city']);
  const state = pickFirstString(propertyItem, ['state']).toUpperCase();
  const zip = pickFirstString(propertyItem, ['postalCode', 'zip']);
  const zoningDistrict = pickFirstString(propertyItem, ['zoneCombinationName']);
  const tokens = {
    '{address}': address,
    '{city}': city,
    '{state}': state,
    '{state_full_name}': getStateFullName(state),
    '{zip}': zip,
    '{folio}': folio,
    '{zoning_district}': zoningDistrict,
  };
  return replaceTemplateTokens(template, tokens);
}

export function setMetaTags(metatags) {
  if (!metatags) {
    return;
  }
  const title = _.get(metatags, 'title');
  if (title) {
    document.title = title;
  }
  const canonicalFromMetatags = _.get(metatags, 'canonical') || _.get(metatags, 'og:url') || _.get(metatags, 'twitter:url');
  const canonicalUrl = canonicalFromMetatags || window.location.origin + window.location.pathname;
  const $canonicalTag = document.querySelector('link[rel="canonical"]');
  if ($canonicalTag) {
    $canonicalTag.setAttribute('href', canonicalUrl);
  }
  else {
    const linkTag = document.createElement('link');
    linkTag.setAttribute('rel', 'canonical');
    linkTag.href = canonicalUrl;
    document.getElementsByTagName('head')[0].prepend(linkTag);
  }
  const header = document.getElementsByTagName('head')[0];
  const upsertMeta = (selector, attrs, content) => {
    if (!content) {
      return;
    }
    const currentTag = document.querySelector(selector);
    if (currentTag) {
      currentTag.setAttribute('content', content);
      return;
    }
    const metaTag = document.createElement('meta');
    Object.entries(attrs).forEach(([attr, value]) => {
      metaTag.setAttribute(attr, value);
    });
    metaTag.content = content;
    header.prepend(metaTag);
  };
  // Open Graph.
  const ogTitle = _.get(metatags, 'og:title');
  if (ogTitle) {
    const $ogTitleTag = document.querySelector('meta[property="og:title"]');
    if ($ogTitleTag) {
      $ogTitleTag.setAttribute('content', ogTitle);
    }
    else {
      const metaTag = document.createElement('meta');
      metaTag.setAttribute('property', 'og:title');
      metaTag.content = ogTitle;
      header.prepend(metaTag);
    }
    // Prefer metatag-provided URL to preserve property short-link canonical.
    const $ogUrlTag = document.querySelector('meta[property="og:url"]');
    if ($ogUrlTag) {
      $ogUrlTag.setAttribute('content', canonicalUrl);
    }
    else {
      const metaTag = document.createElement('meta');
      metaTag.setAttribute('property', 'og:url');
      metaTag.content = canonicalUrl;
      header.prepend(metaTag);
    }
  }
  const ogType = _.get(metatags, 'og:type');
  upsertMeta('meta[property="og:type"]', {property: 'og:type'}, ogType);
  const ogDescription = _.get(metatags, 'og:description');
  upsertMeta('meta[property="og:description"]', {property: 'og:description'}, ogDescription);
  const ogImage = _.get(metatags, 'og:image');
  upsertMeta('meta[property="og:image"]', {property: 'og:image'}, ogImage);
  upsertMeta('meta[property="og:site_name"]', {property: 'og:site_name'}, _.get(metatags, 'og:site_name'));
  upsertMeta('meta[property="og:locale"]', {property: 'og:locale'}, _.get(metatags, 'og:locale'));
  // Twitter.
  const twitterCard = _.get(metatags, 'twitter:card');
  upsertMeta('meta[name="twitter:card"]', {name: 'twitter:card'}, twitterCard);
  const twitterSite = _.get(metatags, 'twitter:site');
  upsertMeta('meta[name="twitter:site"]', {name: 'twitter:site'}, twitterSite);
  upsertMeta('meta[name="twitter:image"]', {name: 'twitter:image'}, _.get(metatags, 'twitter:image'));
  upsertMeta('meta[name="image"]', {name: 'image'}, _.get(metatags, 'image'));
  upsertMeta('meta[name="robots"]', {name: 'robots'}, _.get(metatags, 'robots'));
  upsertMeta('meta[name="author"]', {name: 'author'}, _.get(metatags, 'author'));
  upsertMeta('meta[name="Copyright"]', {name: 'Copyright'}, _.get(metatags, 'Copyright'));

  const description = _.get(metatags, 'description');
  upsertMeta('meta[name="description"]', {name: 'description'}, description);
}

export function apiResponseCheck(response, itemsKey = 'items') {
  const error = _.get(response, ['data', 'error']);
  const items = _.get(response, ['data', itemsKey]);
  if (items) {
    return items;
  }
  else if (error) {
    console.error(error);
  }
  return false;
}

export function prepareUrl(path, search = {}, removeSearches = []) {
  const url = new UriJs(path)
    .search(window.location.search)
    .hash(window.location.hash)
    .setSearch(search)
    .removeSearch(removeSearches)
    .normalize()
    .toString();
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return url === currentUrl ? null : url;
}

export function buildPropertyNavigationUrl({
  groupId, folio, propertyPath, isDifferentMarket, preserveSearch = false,
}) {
  const shortUrl = buildPropertyShortPath(groupId, folio);
  if (shortUrl) {
    // Opening a property within the current market keeps the applied filters/view state alive in
    // the URL; a different-market link must not carry this market's filters onto the new market.
    return preserveSearch ? `${shortUrl}${preserveMapViewSearch()}` : shortUrl;
  }
  if (isPropertyShortPath(propertyPath)) {
    return propertyPath;
  }
  if (isDifferentMarket) {
    return `${propertyPath}?folio=${folio}`;
  }
  return prepareUrl(propertyPath, {folio}, ['property_id']);
}
